import React, { useState, useEffect, useMemo } from 'react';
import { Colors as C } from '../../constants/Colors.js';
import { supabase } from '../../lib/supabase.js';
import { mapListing } from '../../lib/listingsService.js';
import WebTopBar from '../components/WebTopBar.js';
import Icon from '../components/Icon.js';
import VinylCover from '../components/VinylCover.js';
import { Avatar, DCard, BtnGold, Badge } from '../components/MarketplaceAtoms.js';
import { profileInitials } from '../../lib/profileAvatar.js';
import { useProfile } from '../hooks/useProfile.js';
import { GROOVES_PHONE_GATE_USER_MESSAGE, LISTING_FEATURE_COST, profileGroovesBalance } from '../constants/groovesEconomy.js';
import { destacarListingGrooves, fetchGroovesSpendCosts } from '../../lib/groovesSpendService.js';
import SellerPremiumStatsPanel from '../components/SellerPremiumStatsPanel.js';
import {
  sellerMarkTransactionShipped,
  sellerSetTransactionToPartnerMode,
} from '../../lib/transactionsService.js';
import { canSellerEditListing } from '../listingPublicarHydrate.js';
import { sellerModeLabel } from '../constants/partnerLogistics.js';
import { TRANSACTION_LOGISTICS_SELECT } from '../constants/transactionLogistics.js';
import { auctionStatusLabel, isAuctionPendingVerification } from '../constants/auctionStatus.js';
import TransactionShipmentPanel from '../components/TransactionShipmentPanel.js';

function formatEuro(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return '—';
  return `${x.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;
}

function relTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const diff = Date.now() - d.getTime();
  const days = Math.floor(diff / 86400000);
  if (days <= 0) return 'Hoy';
  if (days === 1) return 'Ayer';
  return `Hace ${days} días`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
}

function txDone(t) {
  return !!(t.buyer_confirmed_at != null) || String(t.status || '').toLowerCase() === 'completed';
}

function txCancelled(t) {
  return String(t.status || '').toLowerCase() === 'cancelled';
}

function txInProgress(t) {
  return !txDone(t) && !txCancelled(t);
}

function isVerifierSale(t) {
  const s = String(t?.shipping_type || '').toLowerCase();
  return s.includes('tienda_verificadora') || s.includes('verific');
}

function isPickupSale(t) {
  const s = String(t?.shipping_type || '').toLowerCase();
  return s.includes('punto_recogida') || s.includes('pickup');
}

function isPartnerHubSale(t) {
  return isVerifierSale(t) || isPickupSale(t);
}

function listingFeaturedActive(item) {
  const until = item?.featuredUntil ?? item?.featured_until;
  if (!until) return false;
  return new Date(until).getTime() > Date.now();
}

export default function PageMisVentas({ onBack, onNavigate, user }) {
  const { profile, loading: profileLoading, refetch: refetchProfile } = useProfile(user?.id);
  const [tab, setTab] = useState(0);

  const [loading, setLoading] = useState(!!user?.id);
  const [activeListings, setActiveListings] = useState([]);
  const [auctionListings, setAuctionListings] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [listingById, setListingById] = useState({});
  const [buyerById, setBuyerById] = useState({});
  const [rating, setRating] = useState({ avg: null, count: 0 });
  const [reserveBusyById, setReserveBusyById] = useState({});
  const [shipDraft, setShipDraft] = useState({});
  const [shipBusyId, setShipBusyId] = useState(null);
  const [partnerModeBusyId, setPartnerModeBusyId] = useState(null);
  const [partnerPlaceById, setPartnerPlaceById] = useState({});
  const [featureBusyById, setFeatureBusyById] = useState({});
  const [listingFeatureCost, setListingFeatureCost] = useState(LISTING_FEATURE_COST);

  const reloadTransactions = async () => {
    if (!user?.id) return;
    const { data } = await supabase
      .from('transactions')
      .select(TRANSACTION_LOGISTICS_SELECT)
      .eq('seller_id', user.id)
      .order('created_at', { ascending: false })
      .limit(120);
    if (data) setTransactions(data);
  };

  useEffect(() => {
    if (!user?.id) {
      setActiveListings([]);
      setAuctionListings([]);
      setTransactions([]);
      setListingById({});
      setBuyerById({});
      setRating({ avg: null, count: 0 });
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [listAct, listAuc, txsRes, revRes] = await Promise.all([
          supabase
            .from('listings')
            .select(
              `
              *,
              profiles ( username, avatar_url, is_verified, is_store, partner_active_roles )
            `
            )
            .eq('seller_id', user.id)
            .in('status', ['active', 'reserved'])
            .eq('is_auction', false)
            .order('created_at', { ascending: false })
            .limit(80),
          supabase
            .from('listings')
            .select(
              `
              *,
              profiles ( username, avatar_url, is_verified, is_store, partner_active_roles )
            `
            )
            .eq('seller_id', user.id)
            .eq('is_auction', true)
            .order('created_at', { ascending: false })
            .limit(40),
          supabase
            .from('transactions')
            .select(TRANSACTION_LOGISTICS_SELECT)
            .eq('seller_id', user.id)
            .order('created_at', { ascending: false })
            .limit(120),
          supabase.from('reviews').select('rating').eq('reviewed_id', user.id).eq('is_visible', true),
        ]);

        if (cancelled) return;

        const actRows = listAct.error ? [] : listAct.data ?? [];
        const aucRows = listAuc.error ? [] : listAuc.data ?? [];
        if (listAct.error) console.warn('[PageMisVentas] listings activos:', listAct.error.message);
        if (listAuc.error) console.warn('[PageMisVentas] listings subasta:', listAuc.error.message);

        setActiveListings(actRows.map(mapListing));
        setAuctionListings(aucRows.map(mapListing));

        const txs = txsRes.error ? [] : txsRes.data ?? [];
        if (txsRes.error) console.warn('[PageMisVentas] transactions:', txsRes.error.message);
        setTransactions(txs);

        const listingIds = [...new Set(txs.map(t => t.listing_id).filter(Boolean))];
        const buyerIds = [...new Set(txs.map(t => t.buyer_id).filter(Boolean))];

        const [listJoin, buyersJoin] = await Promise.all([
          listingIds.length
            ? supabase
                .from('listings')
                .select('id,title,artist,condition,price,photos,year')
                .in('id', listingIds)
                .eq('seller_id', user.id)
            : Promise.resolve({ data: [], error: null }),
          buyerIds.length
            ? supabase.from('profiles').select('id, username, avatar_url').in('id', buyerIds)
            : Promise.resolve({ data: [], error: null }),
        ]);

        if (cancelled) return;

        const lmap = {};
        for (const row of listJoin.data ?? []) lmap[row.id] = row;
        setListingById(lmap);

        const bmap = {};
        for (const row of buyersJoin.data ?? []) bmap[row.id] = row;
        setBuyerById(bmap);

        const partnerIds = [
          ...new Set(
            txs.flatMap(t => [t.verifier_partner_id, t.pickup_partner_id].filter(Boolean))
          ),
        ];
        if (partnerIds.length) {
          const { data: vPartners } = await supabase
            .from('partners')
            .select('id, company_name, city, address, maps_url')
            .in('id', partnerIds);
          if (!cancelled) {
            setPartnerPlaceById(Object.fromEntries((vPartners ?? []).map(p => [p.id, p])));
          }
        } else if (!cancelled) {
          setPartnerPlaceById({});
        }

        const revRows = revRes.error ? [] : revRes.data ?? [];
        const count = revRows.length;
        const avg = count ? revRows.reduce((s, r) => s + (Number(r.rating) || 0), 0) / count : null;
        setRating({ avg, count });
        if (revRes.error) console.warn('[PageMisVentas] reviews:', revRes.error.message);
      } catch (e) {
        console.error('[PageMisVentas]', e);
        if (!cancelled) {
          setActiveListings([]);
          setAuctionListings([]);
          setTransactions([]);
          setListingById({});
          setBuyerById({});
          setRating({ avg: null, count: 0 });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    fetchGroovesSpendCosts().then(costs => {
      const c = costs?.destacar_listing?.cost;
      if (Number.isFinite(c) && c > 0) setListingFeatureCost(c);
    });
  }, [user?.id]);

  const groovesTotal = profile ? profileGroovesBalance(profile) : 0;
  const phoneOk = !!profile?.phone_verified_at;

  const handleDestacarListing = async item => {
    if (!item?.id || featureBusyById[item.id]) return;
    if (!phoneOk) {
      window.alert(GROOVES_PHONE_GATE_USER_MESSAGE);
      return;
    }
    if (groovesTotal < listingFeatureCost) {
      window.alert(`Necesitas ${listingFeatureCost} Grooves para destacar este anuncio.`);
      return;
    }
    setFeatureBusyById(p => ({ ...p, [item.id]: true }));
    const res = await destacarListingGrooves(item.id);
    setFeatureBusyById(p => ({ ...p, [item.id]: false }));
    if (!res.ok) {
      window.alert(res.message || 'No se pudo destacar el anuncio.');
      return;
    }
    setActiveListings(prev =>
      prev.map(row => (row.id === item.id ? { ...row, featuredUntil: res.featured_until } : row))
    );
  };

  const completedTx = useMemo(() => transactions.filter(txDone), [transactions]);
  const inProgressTx = useMemo(() => transactions.filter(txInProgress), [transactions]);

  const retenido = useMemo(
    () => inProgressTx.reduce((s, t) => s + (Number(t.amount) || 0), 0),
    [inProgressTx]
  );

  const ventasCount = completedTx.length;
  const valoracionStr =
    loading || profileLoading
      ? '…'
      : rating.count === 0 || rating.avg == null
        ? '—'
        : `${(Math.round(rating.avg * 10) / 10).toFixed(1)}`;

  const tabs = useMemo(
    () => [
      `Activos (${activeListings.length})`,
      `En curso (${inProgressTx.length})`,
      `Completadas (${completedTx.length})`,
      `Subastas (${auctionListings.length})`,
    ],
    [activeListings.length, inProgressTx.length, completedTx.length, auctionListings.length]
  );

  const summaryNums = loading
    ? ['…', '…', '…']
    : [
        String(activeListings.length),
        formatEuro(retenido),
        !profile?.phone_verified_at ? '—' : String(groovesTotal),
      ];

  const listingStubForTx = lid => {
    const row = listingById[lid];
    if (!row) {
      return { id: lid || 'tx', title: 'Anuncio', artist: '—', year: null, condition: '—', price: 0, photos: [] };
    }
    return {
      id: row.id,
      title: row.title,
      artist: row.artist,
      year: row.year,
      condition: row.condition,
      price: Number(row.price),
      photos: row.photos ?? [],
    };
  };

  const openEditListing = item => {
    if (!item?.id || !canSellerEditListing(item)) return;
    onNavigate('publicar', { editListing: item, returnTo: 'misventas' });
  };

  const toggleReserve = async (item, nextReserved) => {
    if (!user?.id || !item?.id) return;
    setReserveBusyById(prev => ({ ...prev, [item.id]: true }));
    try {
      const nextStatus = nextReserved ? 'reserved' : 'active';
      const { error } = await supabase
        .from('listings')
        .update({ status: nextStatus })
        .eq('id', item.id)
        .eq('seller_id', user.id);
      if (error) {
        console.warn('[PageMisVentas] reserva listing:', error.message);
        return;
      }
      setActiveListings(prev =>
        prev.map(x =>
          x.id === item.id
            ? {
                ...x,
                status: nextStatus,
              }
            : x
        )
      );
    } finally {
      setReserveBusyById(prev => ({ ...prev, [item.id]: false }));
    }
  };

  return (
    <div style={{ background: C.dark, minHeight: '100vh', color: '#fff' }}>
      <WebTopBar
        onBack={onBack}
        title="Mis ventas"
        mini
        dark
        user={user}
        onNavigate={onNavigate}
        onSignOut={onBack}
      />
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '20px 20px 60px' }}>
        {!user?.id ? (
          <DCard style={{ padding: 16 }}>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>Inicia sesión para ver tus ventas.</div>
          </DCard>
        ) : null}

        {/* Resumen */}
        <DCard
          style={{
            padding: 16,
            marginBottom: 14,
            background: 'linear-gradient(135deg,#1a1a2e,#0d1a2e)',
            borderColor: 'rgba(200,168,75,0.2)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: '#fff' }}>Resumen del vendedor</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: C.gold }}>
              ★ {valoracionStr}{' '}
              <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>
                ({loading ? '…' : `${ventasCount} venta${ventasCount === 1 ? '' : 's'}`})
              </span>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            {[
              [summaryNums[0], 'Anuncios activos'],
              [summaryNums[1], 'Retenido (en curso)'],
              [summaryNums[2], 'Grooves'],
            ].map(([n, l]) => (
              <div key={l} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 500, color: C.gold }}>{n}</div>
                <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>{l}</div>
              </div>
            ))}
          </div>
          {!profileLoading && profile && !profile.phone_verified_at ? (
            <div
              style={{
                marginTop: 12,
                padding: 10,
                borderRadius: 10,
                background: 'rgba(239,159,39,0.08)',
                border: '0.5px solid rgba(239,159,39,0.25)',
                fontSize: 11,
                color: 'rgba(255,255,255,0.82)',
                lineHeight: 1.45,
              }}>
              <div style={{ marginBottom: 8 }}>{GROOVES_PHONE_GATE_USER_MESSAGE}</div>
              <BtnGold type="button" onClick={() => onNavigate('ajustes', { initialSub: 'cuenta' })} style={{ padding: '7px 12px', fontSize: 11, borderRadius: 8 }}>
                Ir a Ajustes
              </BtnGold>
            </div>
          ) : null}
        </DCard>

        {user?.id ? (
          <SellerPremiumStatsPanel
            profile={profile}
            phoneVerified={!!profile?.phone_verified_at}
            onNavigate={onNavigate}
            onProfileRefresh={refetchProfile}
          />
        ) : null}

        {/* Acciones */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'stretch' }}>
          <BtnGold
            type="button"
            onClick={() => onNavigate('publicar')}
            style={{
              flex: 1,
              minWidth: 0,
              minHeight: 44,
              borderRadius: 10,
              padding: '10px 12px',
              fontSize: 12,
              fontWeight: 600,
              width: '100%',
              boxSizing: 'border-box',
            }}>
            + Nuevo Anuncio
          </BtnGold>
          <button
            type="button"
            onClick={() => onNavigate('subastas', { openNuevaSubasta: true })}
            style={{
              flex: 1,
              minWidth: 0,
              minHeight: 44,
              padding: '10px 12px',
              borderRadius: 10,
              background: 'rgba(200,168,75,0.08)',
              border: `0.5px solid rgba(200,168,75,0.3)`,
              color: C.gold,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              width: '100%',
              boxSizing: 'border-box',
              fontFamily: 'inherit',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
            }}>
            + Nueva Subasta
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '0.5px solid rgba(255,255,255,0.1)', marginBottom: 14 }}>
          {tabs.map((t, i) => (
            <button
              key={t}
              onClick={() => setTab(i)}
              style={{
                padding: '10px 0',
                fontSize: 11,
                color: tab === i ? C.gold : 'rgba(255,255,255,0.4)',
                cursor: 'pointer',
                marginRight: 14,
                border: 'none',
                background: 'transparent',
                borderBottom: `2px solid ${tab === i ? C.gold : 'transparent'}`,
                whiteSpace: 'nowrap',
              }}
            >
              {t}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '28px 0', color: 'rgba(255,255,255,0.35)', fontSize: 12 }}>
            Cargando…
          </div>
        ) : null}

        {tab === 0 &&
          !loading &&
          activeListings.map(item => (
            <DCard key={item.id} style={{ overflow: 'hidden', marginBottom: 10 }}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => onNavigate('ficha', item)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') onNavigate('ficha', item);
                }}
                style={{ display: 'flex', gap: 10, padding: 12, borderBottom: '0.5px solid rgba(255,255,255,0.06)', cursor: 'pointer' }}
              >
                <div style={{ width: 56, height: 56, borderRadius: 9, flexShrink: 0 }}>
                  <VinylCover item={item} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#fff' }}>{item.title}</div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
                    {item.artist} · {item.condition}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 500, color: C.gold, marginTop: 4 }}>{formatEuro(item.price)}</div>
                  <Badge type={item.status === 'reserved' ? 'warn' : 'green'} style={{ marginTop: 4 }}>
                    {item.status === 'reserved' ? 'Reservado' : 'Activo'}
                  </Badge>
                  {listingFeaturedActive(item) ? (
                    <Badge type="gold" style={{ marginTop: 4, marginLeft: 6 }}>
                      Destacado
                    </Badge>
                  ) : null}
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: 14,
                  padding: '8px 12px',
                  borderBottom: '0.5px solid rgba(255,255,255,0.05)',
                  fontSize: 10,
                  color: 'rgba(255,255,255,0.4)',
                }}
              >
                <span>
                  <Icon name="eye" size={11} color="rgba(255,255,255,0.4)" /> {item.views ?? 0} visitas
                </span>
                <span>
                  <Icon name="calendar" size={11} color="rgba(255,255,255,0.4)" /> {relTime(item.createdAt)}
                </span>
              </div>
              <div style={{ display: 'flex' }}>
                <button
                  type="button"
                  onClick={() => onNavigate('ficha', item)}
                  style={{
                    flex: 1,
                    padding: '9px',
                    border: 'none',
                    background: 'transparent',
                    fontSize: 11,
                    cursor: 'pointer',
                    color: C.gold,
                    borderRight: `0.5px solid rgba(255,255,255,0.05)`,
                  }}
                >
                  Ver anuncio
                </button>
                <button
                  type="button"
                  disabled={!!featureBusyById[item.id] || item.status !== 'active'}
                  onClick={() => handleDestacarListing(item)}
                  style={{
                    flex: 1,
                    padding: '9px',
                    border: 'none',
                    background: 'transparent',
                    fontSize: 11,
                    cursor: featureBusyById[item.id] || item.status !== 'active' ? 'not-allowed' : 'pointer',
                    color: listingFeaturedActive(item) ? 'rgba(200,168,75,0.55)' : C.gold,
                    borderRight: `0.5px solid rgba(255,255,255,0.05)`,
                    opacity: item.status !== 'active' ? 0.45 : 1,
                  }}
                >
                  {featureBusyById[item.id] ? '…' : listingFeaturedActive(item) ? 'Renovar ★' : `Destacar ${listingFeatureCost}★`}
                </button>
                <button
                  type="button"
                  onClick={() => openEditListing(item)}
                  disabled={!canSellerEditListing(item)}
                  style={{
                    flex: 1,
                    padding: '9px',
                    border: 'none',
                    background: 'transparent',
                    fontSize: 11,
                    cursor: canSellerEditListing(item) ? 'pointer' : 'not-allowed',
                    color: canSellerEditListing(item) ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.25)',
                    borderRight: `0.5px solid rgba(255,255,255,0.05)`,
                    opacity: canSellerEditListing(item) ? 1 : 0.5,
                  }}
                >
                  Editar
                </button>
                <button
                  type="button"
                  disabled={!!reserveBusyById[item.id]}
                  onClick={() => toggleReserve(item, item.status !== 'reserved')}
                  style={{
                    flex: 1,
                    padding: '9px',
                    border: 'none',
                    background: 'transparent',
                    fontSize: 11,
                    cursor: reserveBusyById[item.id] ? 'wait' : 'pointer',
                    color: item.status === 'reserved' ? 'rgba(255,255,255,0.75)' : C.warn,
                    opacity: reserveBusyById[item.id] ? 0.65 : 1,
                  }}
                >
                  {reserveBusyById[item.id]
                    ? 'Guardando…'
                    : item.status === 'reserved'
                      ? 'Quitar reserva'
                      : 'Reservar'}
                </button>
              </div>
            </DCard>
          ))}

        {tab === 0 && !loading && activeListings.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: 'rgba(255,255,255,0.3)', fontSize: 12 }}>
            No tienes anuncios activos. Publica uno nuevo.
          </div>
        ) : null}

        {tab === 1 &&
          !loading &&
          inProgressTx.map(t => {
            const stub = listingStubForTx(t.listing_id);
            const buyer = t.buyer_id ? buyerById[t.buyer_id] : null;
            const buyerLabel = buyer?.username ? `@${buyer.username}` : 'Comprador';
            const partnerId = t.verifier_partner_id || t.pickup_partner_id;
            const partnerPlace = partnerId ? partnerPlaceById[partnerId] : null;
            const partnerHub = isPartnerHubSale(t);
            const sellerMode = t.seller_to_partner_mode;
            return (
              <DCard key={t.id} style={{ padding: 14, marginBottom: 10 }}>
                <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 8, flexShrink: 0 }}>
                    <VinylCover item={stub} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: '#fff' }}>{stub.title}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 2, flexWrap: 'wrap' }}>
                      <span style={{ flexShrink: 0 }}>Comprador:</span>
                      <Avatar
                        initials={profileInitials(buyer?.username || 'Comprador')}
                        size={22}
                        bg="#534AB7"
                        color="#EEEDFE"
                        imageUrl={buyer?.avatar_url}
                      />
                      <span>{buyerLabel} · {String(t.status || 'pendiente').replace(/_/g, ' ')}</span>
                    </div>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: C.gold }}>{formatEuro(t.amount)}</div>
                </div>
                {partnerHub ? (
                  <div
                    style={{
                      marginBottom: 10,
                      padding: '10px 12px',
                      borderRadius: 10,
                      border: '0.5px solid rgba(200,168,75,0.25)',
                      background: 'rgba(200,168,75,0.06)',
                      fontSize: 11,
                      color: 'rgba(255,255,255,0.7)',
                      lineHeight: 1.5,
                    }}
                  >
                    <strong style={{ color: C.gold }}>
                      {isVerifierSale(t) ? 'Tienda verificadora' : 'Punto de recogida DreamVinyl'}
                    </strong>
                    {sellerMode ? (
                      <>
                        <br />
                        <span style={{ color: 'rgba(255,255,255,0.5)' }}>
                          Tu entrega al partner: {sellerModeLabel(sellerMode)}
                        </span>
                      </>
                    ) : (
                      <>
                        <br />
                        <span style={{ color: 'rgba(255,255,255,0.5)' }}>
                          Elige cómo entregarás el disco al partner:
                        </span>
                        <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                          {[
                            { mode: 'physical', label: 'Llevar en persona' },
                            { mode: 'shipping', label: 'Enviar por mensajería' },
                          ].map(opt => (
                            <button
                              key={opt.mode}
                              type="button"
                              disabled={partnerModeBusyId === t.id}
                              onClick={async () => {
                                setPartnerModeBusyId(t.id);
                                const { error } = await sellerSetTransactionToPartnerMode(t.id, opt.mode);
                                setPartnerModeBusyId(null);
                                if (error) return;
                                const { data } = await supabase
                                  .from('transactions')
                                  .select(TRANSACTION_LOGISTICS_SELECT)
                                  .eq('seller_id', user.id)
                                  .order('created_at', { ascending: false })
                                  .limit(120);
                                if (data) setTransactions(data);
                                else await reloadTransactions();
                              }}
                              style={{
                                padding: '6px 10px',
                                borderRadius: 8,
                                border: '0.5px solid rgba(200,168,75,0.35)',
                                background: 'rgba(200,168,75,0.1)',
                                color: C.gold,
                                fontSize: 10,
                                fontWeight: 600,
                                cursor: partnerModeBusyId === t.id ? 'wait' : 'pointer',
                              }}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                    {sellerMode ? (
                      <>
                        <br />
                        {sellerMode === 'shipping' ? 'Envía el disco a' : 'Lleva el disco a'}{' '}
                        <strong style={{ color: '#fff' }}>
                          {partnerPlace?.company_name ||
                            (isVerifierSale(t)
                              ? 'la tienda elegida por el comprador'
                              : 'el punto elegido por el comprador')}
                        </strong>
                        {partnerPlace?.address ? (
                          <>
                            <br />
                            {partnerPlace.address}
                            {partnerPlace.city ? `, ${partnerPlace.city}` : ''}
                          </>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                ) : t.tracking_number ? (
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginBottom: 8 }}>
                    Tracking: <span style={{ color: 'rgba(255,255,255,0.6)' }}>{t.tracking_number}</span>
                  </div>
                ) : (
                  <div style={{ marginBottom: 10 }}>
                    <label style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', display: 'block', marginBottom: 4 }}>
                      Nº de seguimiento (opcional)
                    </label>
                    <input
                      type="text"
                      value={shipDraft[t.id] ?? ''}
                      onChange={e => setShipDraft(d => ({ ...d, [t.id]: e.target.value }))}
                      placeholder="Correos, SEUR, MRW…"
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        borderRadius: 8,
                        border: '0.5px solid rgba(255,255,255,0.12)',
                        background: 'rgba(255,255,255,0.05)',
                        color: '#fff',
                        fontSize: 11,
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                )}
                {partnerHub ? (
                  <TransactionShipmentPanel
                    transaction={t}
                    viewerRole="seller"
                    currentUserId={user?.id}
                    onUpdated={() => void reloadTransactions()}
                    title="Envío al partner y mensajes de la venta"
                  />
                ) : null}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  {!partnerHub && !t.tracking_number ? (
                    <button
                      type="button"
                      disabled={shipBusyId === t.id}
                      onClick={async () => {
                        setShipBusyId(t.id);
                        const { error } = await sellerMarkTransactionShipped(t.id, shipDraft[t.id] || null);
                        setShipBusyId(null);
                        if (error) return;
                        const { data } = await supabase
                          .from('transactions')
                          .select(TRANSACTION_LOGISTICS_SELECT)
                          .eq('seller_id', user.id)
                          .order('created_at', { ascending: false })
                          .limit(120);
                        if (data) setTransactions(data);
                        else await reloadTransactions();
                      }}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 8,
                        background: C.gold,
                        border: 'none',
                        color: '#0a0a0f',
                        fontSize: 11,
                        fontWeight: 700,
                        cursor: shipBusyId === t.id ? 'wait' : 'pointer',
                      }}
                    >
                      {shipBusyId === t.id ? 'Guardando…' : 'Marcar enviado'}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => onNavigate('chat')}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 8,
                      background: 'rgba(200,168,75,0.1)',
                      border: `0.5px solid rgba(200,168,75,0.3)`,
                      color: C.gold,
                      fontSize: 11,
                      cursor: 'pointer',
                    }}
                  >
                    Chat
                  </button>
                </div>
              </DCard>
            );
          })}

        {tab === 1 && !loading && inProgressTx.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: 'rgba(255,255,255,0.3)', fontSize: 12 }}>
            No hay ventas en curso.
          </div>
        ) : null}

        {tab === 2 &&
          !loading &&
          completedTx.map(t => {
            const stub = listingStubForTx(t.listing_id);
            const buyer = t.buyer_id ? buyerById[t.buyer_id] : null;
            const buyerLabel = buyer?.username ? `@${buyer.username}` : 'Comprador';
            return (
              <DCard key={t.id} style={{ display: 'flex', gap: 10, padding: '11px 12px', marginBottom: 8 }}>
                <div style={{ width: 44, height: 44, borderRadius: 8, flexShrink: 0 }}>
                  <VinylCover item={stub} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: '#fff' }}>{stub.title}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>
                    <Avatar
                      initials={profileInitials(buyer?.username || 'Comprador')}
                      size={22}
                      bg="#534AB7"
                      color="#EEEDFE"
                      imageUrl={buyer?.avatar_url}
                    />
                    <span>{buyerLabel}</span>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: C.green }}>+{formatEuro(t.amount)}</div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 2 }}>{fmtDate(t.buyer_confirmed_at || t.created_at)}</div>
                  <Badge type="gray" style={{ marginTop: 4 }}>
                    Venta
                  </Badge>
                </div>
              </DCard>
            );
          })}

        {tab === 2 && !loading && completedTx.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: 'rgba(255,255,255,0.3)', fontSize: 12 }}>
            Aún no hay ventas completadas.
          </div>
        ) : null}

        {tab === 3 &&
          !loading &&
          auctionListings.map(item => (
            <DCard key={item.id} style={{ overflow: 'hidden', marginBottom: 10 }}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => onNavigate('ficha', item)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') onNavigate('ficha', item);
                }}
                style={{ display: 'flex', gap: 10, padding: 12, borderBottom: '0.5px solid rgba(255,255,255,0.06)', cursor: 'pointer' }}
              >
                <div style={{ width: 56, height: 56, borderRadius: 9, flexShrink: 0 }}>
                  <VinylCover item={item} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#fff' }}>{item.title}</div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
                    {item.artist} · {item.condition}
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                    <span style={{ fontSize: 15, fontWeight: 500, color: C.gold }}>
                      {item.currentBid != null ? `${item.currentBid} €` : `${item.price} €`}
                    </span>
                    <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', alignSelf: 'center' }}>puja / salida</span>
                  </div>
                  <Badge type="purple" style={{ marginTop: 4 }}>
                    Subasta · {auctionStatusLabel(item.status)}
                  </Badge>
                  {isAuctionPendingVerification(item.status) ? (
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.42)', marginTop: 6, lineHeight: 1.45 }}>
                      Lleva el disco a la tienda verificadora. Se publicará al verificar.
                    </div>
                  ) : null}
                </div>
              </div>
              {canSellerEditListing(item) ? (
                <div style={{ display: 'flex', borderTop: '0.5px solid rgba(255,255,255,0.06)' }}>
                  <button
                    type="button"
                    onClick={() => openEditListing(item)}
                    style={{
                      flex: 1,
                      padding: '9px',
                      border: 'none',
                      background: 'transparent',
                      fontSize: 11,
                      cursor: 'pointer',
                      color: C.gold,
                      borderRight: `0.5px solid rgba(255,255,255,0.05)`,
                    }}
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => onNavigate('ficha', item)}
                    style={{
                      flex: 1,
                      padding: '9px',
                      border: 'none',
                      background: 'transparent',
                      fontSize: 11,
                      cursor: 'pointer',
                      color: 'rgba(255,255,255,0.65)',
                    }}
                  >
                    Ver subasta
                  </button>
                </div>
              ) : null}
            </DCard>
          ))}

        {tab === 3 && !loading && auctionListings.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: 'rgba(255,255,255,0.3)', fontSize: 12 }}>
            No tienes anuncios marcados como subasta.
          </div>
        ) : null}
      </div>
    </div>
  );
}
